import React from 'react'
import { Icon } from 'iconsax-react'
import { useTheme } from '@mui/material'

import Tooltip from '@SonarCX/components/atoms/Tooltip'
import CommonModal from '../CommonModal'
import Button from '@SonarCX/components/atoms/Button2'
import ButtonSecondary from '@SonarCX/components/atoms/ButtonSecondary'

import useStyles from '../commonStyles'

type CommonButtonProps = {
  title: string
  onClick?: () => void
  onClose?: () => void
  onAccept?: () => void
  Icon: Icon
  confirmText?: string
  confirmDisabled?: boolean
  confirmDisabledReason?: string
  cancelText?: string
  children?: React.ReactNode
}

const CommonButton: React.FC<CommonButtonProps> = ({
  title,
  onClick,
  onClose,
  onAccept,
  Icon,
  confirmText = 'Save',
  confirmDisabled,
  confirmDisabledReason,
  cancelText = 'Cancel',
  children
}) => {
  const theme = useTheme()

  const [open, setOpen] = React.useState(false)

  const classes = useStyles()

  const handleOpen = React.useCallback(() => {
    if (onClick) {
      onClick()
      return
    }

    setOpen(true)
  }, [onClick])

  const handleClose = React.useCallback(() => {
    setOpen(false)
  }, [])

  const handleCancel = React.useCallback(() => {
    if (onClose) {
      onClose()
    }

    handleClose()
  }, [onClose, handleClose])

  const handleAccept = React.useCallback(() => {
    if (onAccept) {
      onAccept()
    }

    handleClose()
  }, [onAccept, handleClose])

  return (
    <>
      <Tooltip title={title} placement='bottom' arrow>
        <div className={classes.root}>
          <Icon
            variant='Bulk'
            size={theme.size.base}
            color={theme.palette.primary.main}
            onClick={handleOpen}
          />
        </div>
      </Tooltip>

      {open && (
        <CommonModal title={title} onClose={handleClose}>
          {children}

          <div className={classes.endButtons}>
            <Button onClick={handleCancel}>{cancelText}</Button>

            <Tooltip title={confirmDisabled ? confirmDisabledReason : null}>
              <div>
                <ButtonSecondary
                  disabled={confirmDisabled}
                  onClick={handleAccept}
                  className={classes.yesButton}
                >
                  {confirmText}
                </ButtonSecondary>
              </div>
            </Tooltip>
          </div>
        </CommonModal>
      )}
    </>
  )
}

export default CommonButton
